import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode, SyntheticEvent } from 'react'
import classNames from 'classnames'
import { ReactComponent as InformationIcon } from '../../assets/icons/utility/information.svg'
import './tooltip.scss'

let tooltipIdCounter = 0
const createTooltipId = () => {
  tooltipIdCounter += 1
  return tooltipIdCounter
}

type TooltipProps = {
  title: string
  description?: ReactNode
  position?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
}

const Tooltip = ({ title, description, position = 'top', className }: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [placement, setPlacement] = useState<'top' | 'bottom'>(position === 'bottom' ? 'bottom' : 'top')
  const [coords, setCoords] = useState({ top: 0, left: 0 })
  const tooltipIdRef = useRef<string | null>(null)
  const triggerRef = useRef<HTMLDivElement | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const closeTimeoutRef = useRef<number | null>(null)

  if (!tooltipIdRef.current) {
    tooltipIdRef.current = `xsd-tooltip-${createTooltipId()}`
  }

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  const openTooltip = () => {
    clearCloseTimeout()
    setIsOpen(true)
  }

  const scheduleClose = () => {
    clearCloseTimeout()
    closeTimeoutRef.current = window.setTimeout(() => setIsOpen(false), 100)
  }

  const stopPropagation = (event: SyntheticEvent) => {
    event.stopPropagation()
  }

  useLayoutEffect(() => {
    if (!isOpen) return
    if (typeof window === 'undefined') return

    const updatePosition = () => {
      const trigger = triggerRef.current
      const tooltip = tooltipRef.current
      if (!trigger || !tooltip) return

      const triggerRect = trigger.getBoundingClientRect()
      const tooltipRect = tooltip.getBoundingClientRect()
      const margin = 8
      const gap = 6
      const arrowOffset = 18

      let nextPlacement: 'top' | 'bottom' = 'top'
      let top = triggerRect.top - tooltipRect.height - gap - arrowOffset
      if (position === 'bottom' || top < margin) {
        nextPlacement = 'bottom'
        top = triggerRect.bottom + gap + arrowOffset
      }

      let left = triggerRect.left + triggerRect.width / 2
      const halfWidth = tooltipRect.width / 2
      left = Math.max(margin + halfWidth, Math.min(left, window.innerWidth - halfWidth - margin))

      setPlacement(nextPlacement)
      setCoords({ top, left })
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen])

  const portal =
    isOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
            className={classNames('xsd-tooltip-portal', `msds-popover--${placement}`)}
            style={{ top: coords.top, left: coords.left }}
            onMouseEnter={openTooltip}
            onMouseLeave={scheduleClose}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}>
            <div className={classNames('msds-popover', 'xsd-tooltip-portal__popover', className)}>
              <div
                id={tooltipIdRef.current}
                role="tooltip"
                className="msds-popover__item msds-text-gray-10"
                ref={tooltipRef}>
                <div className="msds-popover__content">
                  <div className="msds-popover__header mb-2">
                    <InformationIcon className="msds-icon xsd-info-tooltip__icon msds-text-alt-blue-1" aria-hidden="true" />
                    <div className="ms-2">{title}</div>
                  </div>
                  {description && <div className="msds-popover__description mb-1">{description}</div>}
                </div>
                <div className="msds-popover__arrow"></div>
              </div>
            </div>
          </div>,
          document.body
        )
      : null

  return (
    <div
      className={classNames(
        'd-inline-block',
        'msds-popover',
        `msds-popover--${position}`,
        'xsd-info-tooltip',
        className
      )}
      onMouseEnter={openTooltip}
      onMouseLeave={scheduleClose}>
        <div
          className="xsd-info-tooltip__trigger"
          aria-describedby={tooltipIdRef.current}
          ref={triggerRef}
          onClick={stopPropagation}
          onMouseDown={stopPropagation}>
          <InformationIcon className="msds-icon xsd-info-tooltip__icon msds-text-alt-blue-1" aria-hidden="true" />
        </div>
      {portal}
    </div>
  )
}

export default Tooltip
